/**
 * useSyncAttention — FT-577 (2026-11-01). The single source of truth for the
 * user-visible sync status (header badge `SyncStatusIndicator`, and the
 * Account → Sync panel).
 *
 * Before FT-577 the header badge only knew "offline" / "N pending" — a change
 * that had FAILED (incl. a photo permanently rejected by the server) was not
 * pending, so the badge fell through to a green "Synced" while data was lost.
 *
 * Status precedence (first match wins):
 *   offline  → no network
 *   failed   → something permanently failed to upload (retries exhausted), or
 *              a photo was saved but could not be queued for upload
 *   syncing  → changes waiting to upload
 *   retrying → something failed and will be retried automatically
 *   synced   → genuinely nothing outstanding
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { getOutboxCount, OUTBOX_STATUS_EVENT } from '../lib/outbox';
import { getOutboxFailureSummary, retryAllFailedItems } from '../lib/outboxFailures';
import { getPhotoEnqueueFailureCount } from '../lib/photoEnqueueFailures';

const REFRESH_INTERVAL_MS = 30000;

const EMPTY = {
  pendingCount: 0,
  permanentCount: 0,
  permanentPhotoCount: 0,
  retryingCount: 0,
  retryingPhotoCount: 0,
  enqueueFailedPhotoCount: 0,
};

/** Pure — exported for tests. */
export const deriveSyncStatus = ({
  isOnline,
  pendingCount = 0,
  permanentCount = 0,
  retryingCount = 0,
  enqueueFailedPhotoCount = 0,
}) => {
  if (!isOnline) return 'offline';
  if (permanentCount > 0 || enqueueFailedPhotoCount > 0) return 'failed';
  if (pendingCount > 0) return 'syncing';
  if (retryingCount > 0) return 'retrying';
  return 'synced';
};

const countWord = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * Plain-English "2 photos and 1 other change". Exported for tests.
 * @param {number} photos
 * @param {number} total  all items incl. photos
 */
export const describeItems = (photos, total) => {
  const others = Math.max(0, total - photos);
  if (photos > 0 && others > 0) {
    return `${countWord(photos, 'photo', 'photos')} and ${countWord(others, 'other change', 'other changes')}`;
  }
  if (photos > 0) return countWord(photos, 'photo', 'photos');
  return countWord(others, 'change', 'changes');
};

/**
 * Plain-English explanation of what needs attention (empty string if nothing).
 * Exported for tests.
 */
export const describeAttention = (s) => {
  const parts = [];
  if (s.permanentCount > 0) {
    const what = describeItems(s.permanentPhotoCount, s.permanentCount);
    const many = s.permanentCount !== 1;
    parts.push(`${what} couldn't be uploaded to the cloud. ${many ? "They're" : "It's"} still saved on this device.`);
  }
  if (s.enqueueFailedPhotoCount > 0) {
    const many = s.enqueueFailedPhotoCount !== 1;
    parts.push(
      `${countWord(s.enqueueFailedPhotoCount, 'photo', 'photos')} saved on this device ${many ? "haven't" : "hasn't"} been queued for upload yet. `
      + `${many ? "They'll" : "It'll"} be retried next time the app is opened.`,
    );
  }
  if (s.retryingCount > 0) {
    const what = describeItems(s.retryingPhotoCount, s.retryingCount);
    const many = s.retryingCount !== 1;
    parts.push(`${what} didn't upload. ${many ? "They'll" : "It'll"} try again automatically.`);
  }
  return parts.join(' ');
};

/**
 * FT-577 review fix: plain-English note for failed NON-photo changes, which
 * "Try again" deliberately does not re-send (re-sending an old change could
 * overwrite newer data saved since on another device). Exported for tests.
 */
export const describeSupportNote = (otherPermanentCount) => {
  if (!otherPermanentCount) return '';
  const many = otherPermanentCount !== 1;
  return `${countWord(otherPermanentCount, 'non-photo change', 'non-photo changes')} can't be re-sent from here, `
    + `because ${many ? 'they' : 'it'} could overwrite newer changes made on another device. Please contact support.`;
};

/**
 * FT-577 review fix: what to tell the user after "Try again" (empty string if
 * everything that was offered was re-sent). Exported for tests.
 * @param {{ requeued?:number, notRetriedPhotos?:number }} result
 */
export const describeRetryResult = (result) => {
  const skipped = result?.notRetriedPhotos || 0;
  if (!skipped) return '';
  const many = skipped !== 1;
  return `${countWord(skipped, 'photo', 'photos')} couldn't be re-sent: ${many ? "they're" : "it's"} no longer on this device, `
    + `${many ? 'were' : 'was'} deleted, or ${many ? 'are' : 'is'} still too large. Please contact support if ${many ? 'they are' : 'it is'} still needed.`;
};

export function useSyncAttention() {
  const [state, setState] = useState(() => ({
    ...EMPTY,
    isOnline: typeof navigator === 'undefined' ? true : navigator.onLine,
    // false until the first outbox read completes (lets tests / E2E bots wait
    // for a real status rather than the initial default).
    loaded: false,
  }));
  const [retrying, setRetrying] = useState(false);
  const [retryNote, setRetryNote] = useState('');
  const mountedRef = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const [pendingCount, summary] = await Promise.all([
        getOutboxCount(),
        getOutboxFailureSummary(),
      ]);
      if (!mountedRef.current) return;
      setState({
        pendingCount,
        permanentCount: summary.permanentCount,
        permanentPhotoCount: summary.permanentPhotoCount,
        retryingCount: summary.retryingCount,
        retryingPhotoCount: summary.retryingPhotoCount,
        enqueueFailedPhotoCount: getPhotoEnqueueFailureCount(),
        isOnline: navigator.onLine,
        loaded: true,
      });
    } catch (error) {
      // eslint-disable-next-line no-console -- status read failure must be visible
      console.error('[SYNC STATUS] Failed to update:', error);
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    refresh();
    const interval = setInterval(refresh, REFRESH_INTERVAL_MS);
    const onChange = () => { refresh(); };
    const onOffline = () => setState((prev) => ({ ...prev, isOnline: false }));
    window.addEventListener('outboxChanged', onChange);
    window.addEventListener('outboxProcessed', onChange);
    window.addEventListener(OUTBOX_STATUS_EVENT, onChange);
    window.addEventListener('online', onChange);
    window.addEventListener('offline', onOffline);
    return () => {
      mountedRef.current = false;
      clearInterval(interval);
      window.removeEventListener('outboxChanged', onChange);
      window.removeEventListener('outboxProcessed', onChange);
      window.removeEventListener(OUTBOX_STATUS_EVENT, onChange);
      window.removeEventListener('online', onChange);
      window.removeEventListener('offline', onOffline);
    };
  }, [refresh]);

  /**
   * "Try again": re-sends failed PHOTO uploads that are safe to re-send
   * (rebuilt from the device); the regular drain uploads them. Never re-sends
   * other changes — see outboxFailures.retryAllFailedItems.
   */
  const retryNow = useCallback(async () => {
    setRetrying(true);
    setRetryNote('');
    try {
      const result = await retryAllFailedItems();
      if (mountedRef.current) setRetryNote(describeRetryResult(result));
    } finally {
      await refresh();
      if (mountedRef.current) setRetrying(false);
    }
  }, [refresh]);

  const status = deriveSyncStatus(state);
  // FT-577 review fix: "Try again" only ever re-sends photo uploads, so only
  // offer it when a photo upload has failed.
  const canRetry = state.permanentPhotoCount > 0 || state.retryingPhotoCount > 0;
  const otherPermanentCount = Math.max(0, state.permanentCount - state.permanentPhotoCount);

  return {
    ...state,
    status,
    attentionMessage: describeAttention(state),
    needsAttention: status === 'failed' || status === 'retrying'
      || (status === 'offline' && (state.permanentCount > 0 || state.enqueueFailedPhotoCount > 0)),
    canRetry,
    retryNote: canRetry ? retryNote : '',
    supportNote: describeSupportNote(otherPermanentCount),
    retryInFlight: retrying,
    retryNow,
    refresh,
  };
}

export default useSyncAttention;
